import { useAppConfiguration } from '#app/hooks/useAppConfiguration.js';
import { parseJson } from '#app/utilities/parseJson.js';
import { useCallback, useState } from 'react';
import { type Area } from 'react-easy-crop';

export type CloudinarySignatureOptions = {
  apiKey: string;
  signature: string;
  timestamp: string;
  transformation: string;
};

// NOTE: UploadImagePreset and UploadVideoPreset should not share
// any keys in common. -@erictaylor

export type UploadImagePreset =
  | 'avatar'
  | 'defaultImage'
  | 'feedPost'
  | 'logo'
  | 'profileCover'
  | 'projectCover'
  | 'wintour';

export type UploadPayload = {
  /**
   * Whether the image is animated (ie animated GIF or WEBP)
   */
  animated: boolean;
  audioCodecName?: string;
  checksum?: null | string;
  duration?: number;
  /**
   * The original height of file image/video
   * @example 1080
   */
  height: number;
  isAudio?: boolean;
  /**
   * The original format of the image/video
   * @example 'jpg'
   */
  originalFormat: string;
  resourceType: 'image' | 'raw' | 'video';
  sizeBytes: number;
  /**
   * The Cloudinary `public_id` of the resource.
   */
  uid: string;
  videoCodecName?: string;
  /**
   * The original height of file image/video
   * @example 1920
   */
  width: number;
};

export type UseUploadFunction<R extends UploadResourceType> = (
  options: UploadOptions<R>,
) => void;

type CloudinaryErrorResponse = {
  error: {
    message: string;
  };
};

type CloudinaryResponse = CloudinaryErrorResponse | CloudinaryValidResponse;

/**
 * Generated this type from a response.
 * Could change at anytime. Verify with network requests.
 */
type CloudinaryValidResponse = {
  access_mode: string;
  asset_id: string;
  audio?: {
    codec: string;
  };
  bytes: number;
  created_at: string;
  duration?: number;
  etag: string;
  format: string;
  height: number;
  is_audio?: boolean;
  original_filename: string;
  pages?: number;
  placeholder: boolean;
  public_id: string;
  resource_type: 'image' | 'raw' | 'video';
  secure_url: string;
  signature: string;
  tags?: null | string[];
  type: string;
  url: string;
  version: number;
  version_id: string;
  video?: {
    codec: string;
  };
  width: number;
};

type UploadAutoPreset = 'defaultAuto';

type UploadOptions<Resource extends UploadResourceType> = {
  cropArea?: Area;
  file: Blob | File | string;
  onCompleted: (payload: UploadPayload, previewUrl: string) => void;
  onError: (error: Error) => void;
  /**
   * Called at the end of uploading, regardless of success or failure.
   * Useful for cleanup.
   */
  onFinally?: VoidFunction;
  preset: Resource extends 'image'
    ? UploadImagePreset
    : Resource extends 'video'
      ? UploadVideoPreset
      : UploadAutoPreset;
  signatureOptions?: CloudinarySignatureOptions | null;
};

type UploadPresetConfig = {
  /**
   * An array of mime/types that are allowed for given preset.
   */
  allowedFormats: string[];
  /**
   * This is the name of the upload preset. Pass this to `upload_preset`.
   */
  name: string;
};

type UploadResourceType = 'auto' | 'image' | 'video';

type UploadVideoPreset = 'defaultVideo';

const DEFAULT_IMAGE_FORMATS = [
  'image/avif',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/jpeg',
  'image/png',
  'image/webp',
];

export const UPLOAD_IMAGE_PRESETS: {
  [preset in UploadImagePreset]: UploadPresetConfig;
} = {
  /**
   * Use this when uploading user avatars.
   */
  avatar: {
    allowedFormats: DEFAULT_IMAGE_FORMATS,
    name: 'CWA_IMAGE_AVATAR',
  },
  defaultImage: {
    allowedFormats: DEFAULT_IMAGE_FORMATS,
    name: 'CWA_IMAGE_DEFAULT',
  },
  /**
   * Used when uploading images via rich text posts.
   */
  feedPost: {
    allowedFormats: DEFAULT_IMAGE_FORMATS,
    name: 'CWA_IMAGE_FEED_POST',
  },
  /**
   * Used when users upload a custom "company" logo.
   */
  logo: {
    allowedFormats: DEFAULT_IMAGE_FORMATS,
    name: 'CWA_IMAGE_LOGO',
  },
  /**
   * Use this when uploading a user profile banner.
   */
  profileCover: {
    allowedFormats: DEFAULT_IMAGE_FORMATS,
    name: 'CWA_IMAGE_PROFILE_COVER',
  },
  /**
   * Use this when uploading a project cover.
   */
  projectCover: {
    allowedFormats: DEFAULT_IMAGE_FORMATS,
    name: 'CWA_IMAGE_PROJECT_COVER',
  },
  /**
   * This is used for all images added to Wintour content
   */
  wintour: {
    allowedFormats: DEFAULT_IMAGE_FORMATS,
    name: 'CWA_IMAGE_WINTOUR',
  },
};

const UPLOAD_VIDEO_PRESETS: {
  [preset in UploadVideoPreset]: UploadPresetConfig;
} = {
  defaultVideo: {
    allowedFormats: [],
    name: 'default_video',
  },
};

const videoPresets = Object.keys(UPLOAD_VIDEO_PRESETS);

const isVideoPreset = (
  preset: UploadImagePreset | UploadVideoPreset,
): preset is UploadVideoPreset => {
  return videoPresets.includes(preset);
};

const UPLOAD_AUTO_PRESETS: {
  [preset in UploadAutoPreset]: UploadPresetConfig;
} = {
  defaultAuto: {
    allowedFormats: [],
    name: 'CWA_AUTO',
  },
};

const autoPresets = Object.keys(UPLOAD_AUTO_PRESETS);

const isAutoPreset = (
  preset: UploadAutoPreset | UploadImagePreset | UploadVideoPreset,
): preset is UploadAutoPreset => {
  return autoPresets.includes(preset);
};

const isResponseError = (
  response: CloudinaryResponse,
): response is CloudinaryErrorResponse => {
  return 'error' in response && response.error !== null;
};

const getUploadPreset = (
  preset: UploadAutoPreset | UploadImagePreset | UploadVideoPreset,
): UploadPresetConfig => {
  if (isAutoPreset(preset)) {
    return UPLOAD_AUTO_PRESETS[preset];
  }

  if (isVideoPreset(preset)) {
    return UPLOAD_VIDEO_PRESETS[preset];
  }

  return UPLOAD_IMAGE_PRESETS[preset];
};

const getCustomCoordinates = (area: Area): string => {
  return [area.x, area.y, area.width, area.height].toString();
};

export const useUpload = <Resource extends UploadResourceType>(
  resourceType: Resource,
): [
  upload: UseUploadFunction<Resource>,
  isUploading: boolean,
  progress: number,
  resetProgress: () => void,
] => {
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);

  const { fileUploadApiUrl, playwrightTest } = useAppConfiguration();

  const upload = useCallback<UseUploadFunction<Resource>>(
    async ({
      cropArea,
      file,
      onCompleted,
      onError,
      onFinally,
      preset,
      signatureOptions,
    }) => {
      if (playwrightTest) {
        onCompleted(
          {
            animated: false,
            height: 200,
            originalFormat: 'png',
            resourceType: 'image',
            sizeBytes: 1_042,
            uid: '__playwright__736b518dee06cd1e433ced85e3512c5863e546379cbd73d0ca3cf5a64a3b00dc',
            width: 200,
          },
          'https://media.contra.com/image/upload/v1725975864/__playwright__736b518dee06cd1e433ced85e3512c5863e546379cbd73d0ca3cf5a64a3b00dc.png',
        );
        onFinally?.();
        return;
      }

      setIsUploading(true);

      try {
        if (!fileUploadApiUrl) {
          throw new Error('Invalid file upload configuration.');
        }

        const uploadUrl = fileUploadApiUrl + `/${resourceType}/upload`;

        const formData = new FormData();
        formData.append('file', file);

        if (signatureOptions) {
          const { apiKey, signature, timestamp, transformation } =
            signatureOptions;

          formData.append('api_key', apiKey);
          formData.append('signature', signature);
          formData.append('transformation', transformation);
          formData.append('timestamp', timestamp);
        } else if (cropArea) {
          // This requires our upload preset to have an "Incoming Transformation" of "c_crop,g_custom"
          // See: https://support.cloudinary.com/hc/en-us/articles/115002588752-Cropping-images-and-custom-coordinates-via-the-Upload-Widget
          formData.append('custom_coordinates', getCustomCoordinates(cropArea));
        }

        if (preset) {
          const fileUploadPreset = getUploadPreset(preset);
          formData.append('upload_preset', fileUploadPreset.name);
        }

        const request = new XMLHttpRequest();
        request.open('POST', uploadUrl);

        // Upload Progress Tracking
        request.upload.addEventListener('progress', (progressEvent) => {
          setProgress((progressEvent.loaded / progressEvent.total) * 100);
        });

        // Request Completion
        request.addEventListener('load', () => {
          // See: https://cloudinary.com/documentation/image_upload_api_reference#sample_response
          // Careful! The docs are totally outdated and the fields names in the response may differ!
          const jsonResponse: CloudinaryResponse = parseJson(
            request.responseText,
          );

          if (isResponseError(jsonResponse)) {
            onError(new Error(jsonResponse.error.message));
          } else {
            const payload: UploadPayload = {
              animated: (jsonResponse?.pages ?? 0) > 1,
              audioCodecName: jsonResponse.audio?.codec,
              checksum: jsonResponse.etag,
              duration: jsonResponse.duration,
              height: jsonResponse.height,
              isAudio: jsonResponse.is_audio,
              originalFormat: jsonResponse.format,
              resourceType: jsonResponse.resource_type,
              sizeBytes: jsonResponse.bytes,
              uid: jsonResponse.public_id,
              videoCodecName: jsonResponse.video?.codec,
              width: jsonResponse.width,
            };

            onCompleted(payload, jsonResponse.secure_url);
          }

          setIsUploading(false);
          onFinally?.();
        });

        // Request Error
        request.addEventListener('error', () => {
          setIsUploading(false);
          onError(new Error(request.statusText));
          onFinally?.();
        });

        request.send(formData);
      } catch (error) {
        onError(error);
        onFinally?.();
      }
    },
    [fileUploadApiUrl, playwrightTest, resourceType],
  );

  const resetProgress = () => {
    setProgress(0);
  };

  return [upload, isUploading, progress, resetProgress];
};
